import React, { useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Card, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from 'src/auth';
import { AlineAvatar, AlineTooltip, NumCopies, Spacer } from 'src/components';
import { descriptionMaxLines, titleMaxLines } from 'src/components/community';
import { AlineLogoIcon } from 'src/components/icons/AlineLogoIcon';
import { CompletedIcon } from 'src/components/icons/CompletedIcon';
import { DownloadIcon } from 'src/components/icons/DownloadIcon';
import { useCurrentUserId } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { useDispatchButtonSnack } from 'src/helpers';
import { useImportCommunityWorkflow } from 'src/hooks';
import { createClampStyles } from 'src/theme';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: 320,
  },
  cardHeader: {
    alignItems: 'center',
    display: 'flex',
  },
  cardTitle: {
    ...createClampStyles(titleMaxLines),
  },
  completedIcon: {
    color: theme.appColors.accent2,
  },
  description: {
    flexGrow: 1,
  },
  descriptionText: {
    ...createClampStyles(descriptionMaxLines),
    color: theme.appColors.placeholderType,
    fontSize: 12,
    fontWeight: 400,
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  owner: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.25),
    // So the owner container doesn't jump when the import progress spinner renders.
    height: 40,
  },
}));

export interface CommunityWorkflowCardWorkflow extends Pick<
  Apollo.Workflow,
  'description' | 'id' | 'name' | 'numTimesImported' | 'requesterImportedWorkflowIds' | '__typename'
> {
  owner: UserSubset;
}

export function CommunityWorkflowCard({
  workflow,
}: {
  workflow: CommunityWorkflowCardWorkflow;
}): JSX.Element | null {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [raised, setRaised] = useState<boolean>(false);
  const dataCy = workflow.requesterImportedWorkflowIds.length
    ? Select.community.workflows.importedCard
    : Select.community.workflows.nonImportedCard;

  return (
    <Card
      className={classes.card}
      data-cy={dataCy}
      raised={raised}
      onClick={() => navigate(link.CommunityWorkflowDetailTabSummary({ workflowId: workflow.id }))}
      onMouseEnter={() => setRaised(true)}
      onMouseLeave={() => setRaised(false)}
    >
      <div className={classes.cardHeader}>
        <AlineLogoIcon />
        <Spacer />
        <NumCopies numCopies={workflow.numTimesImported} />
      </div>
      <Typography className={classes.cardTitle} variant="subtitle1">
        {workflow.name || 'Untitled'}
      </Typography>
      <div className={classes.description}>
        <Typography className={classes.descriptionText}>
          {workflow.description || 'No description'}
        </Typography>
      </div>
      <div className={classes.owner}>
        <AlineAvatar avatarInfo={workflow.owner} />
        <Typography variant="body1">{workflow.owner.displayName}</Typography>
        <Spacer />
        <ImportButton workflow={workflow} />
      </div>
    </Card>
  );
}

export function CommunityWorkflowLoadingCard(): JSX.Element {
  const { classes } = useStyles();

  return (
    <Card className={classes.card} style={{ maxHeight: 304 }}>
      <div className={classes.cardHeader}>
        <Skeleton height={32} variant="circular" width={32} />
        <Spacer />
        <Typography>
          <Skeleton variant="text" width={48} />
        </Typography>
      </div>
      <Typography className={classes.cardTitle}>
        <Skeleton variant="text" width={144} />
      </Typography>
      <div className={classes.description}>
        {Array.from({ length: 2 }).map((_v, index) => {
          return (
            <Typography key={index} className={classes.descriptionText}>
              <Skeleton variant="text" width={200} />
            </Typography>
          );
        })}
      </div>
      <div className={classes.owner}>
        <Skeleton height={32} variant="circular" width={32} />
        <Skeleton variant="text" width={80} />
      </div>
    </Card>
  );
}

interface ImportButtonWorkflow extends Pick<
  Apollo.Workflow,
  'id' | 'requesterImportedWorkflowIds' | '__typename'
> {
  owner: Pick<Apollo.User, 'id' | '__typename'>;
}

function ImportButton({ workflow }: { workflow: ImportButtonWorkflow }) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const currentUserId = useCurrentUserId();
  const dispatchSuccessSnack = useDispatchButtonSnack<Pick<Apollo.Workflow, 'id' | '__typename'>>({
    autoHideDurationMS: 7500,
    buttonText: 'Open',
    message: 'Workflow successfully imported.',
    onClick: (e, importedWorkflow) => {
      e.stopPropagation();
      if (importedWorkflow)
        navigate(
          link.WorkflowDetailTabSummary({
            workflowId: importedWorkflow.id,
          })
        );
    },
  });
  const { importWorkflow, loading: importingWorkflow } = useImportCommunityWorkflow(workflow.id);
  const { guardedCallback: handleImportWorkflow } = useAuthGuard(async () => {
    const { data } = await importWorkflow();
    const importedWorkflow = data?.importCommunityWorkflow?.workflow ?? null;
    if (importedWorkflow) dispatchSuccessSnack(workflow);
  });
  const isOwner = workflow.owner.id === currentUserId;
  const requesterAlreadyImported = isOwner || Boolean(workflow.requesterImportedWorkflowIds.length);

  if (importingWorkflow) return <CircularProgress color="primary" size={20} />;

  if (requesterAlreadyImported)
    return (
      <AlineTooltip arrow placement="top" title="View imported workflow">
        <IconButton
          data-cy={Select.community.workflows.card.viewImportedButton}
          size="small"
          onClick={e => {
            e.stopPropagation();
            navigate(link.WorkflowList());
          }}
        >
          <CompletedIcon className={classes.completedIcon} />
        </IconButton>
      </AlineTooltip>
    );

  return (
    <AlineTooltip arrow placement="top" title="Import">
      <IconButton
        data-cy={Select.community.workflows.card.importButton}
        size="small"
        onClick={async e => {
          e.stopPropagation();
          handleImportWorkflow();
        }}
      >
        <DownloadIcon />
      </IconButton>
    </AlineTooltip>
  );
}
